import { Stack, Typography } from "@mui/material";
import { Dialog } from "components/Dialog";
import { FormTextField } from "components/Forms/FormTextField";
import { useEffect } from "react";
import { FormProvider, useForm } from "react-hook-form";

export type WasteVendorFormType = {
  name: string;
};

interface WasteVendorDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: WasteVendorFormType) => Promise<void> | void;
  saving: boolean;
  duplicateName?: string | null;
}

const RESERVED_NAMES = new Set(["self"]);

export const WasteVendorDialog = ({
  open,
  onClose,
  onSave,
  saving,
  duplicateName,
}: WasteVendorDialogProps) => {
  const form = useForm<WasteVendorFormType>({
    defaultValues: { name: "" },
    mode: "onChange",
  });

  const {
    control,
    handleSubmit,
    trigger,
    formState: { isValid, isDirty },
  } = form;

  useEffect(() => {
    if (duplicateName) {
      void trigger("name");
    }
  }, [duplicateName, trigger]);

  const onSubmit = async (data: WasteVendorFormType) => {
    await onSave({ name: data.name.trim() });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      title="Add Waste Vendor"
      primaryActionButtonProps={{
        onClick: handleSubmit(onSubmit),
        disabled: !isValid || !isDirty || saving,
        loading: saving,
        saveText: "Add Vendor",
      }}
    >
      <FormProvider {...form}>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            The new vendor will appear in the OneSchema Vendor Waste Manifest picklist the next time
            a staff import is initialized.
          </Typography>
          <FormTextField
            name="name"
            label="Vendor Name"
            control={control}
            disabled={saving}
            inputProps={{ autoFocus: true }}
            rules={{
              required: "Vendor name is required",
              validate: (value: string) => {
                const trimmed = value?.trim() ?? "";
                if (trimmed.length < 2) return "Vendor name must be at least 2 characters";
                if (trimmed.length > 255) return "Vendor name cannot exceed 255 characters";
                if (RESERVED_NAMES.has(trimmed.toLowerCase())) {
                  return '"Self" is a reserved name used by OneSchema to represent the importing tenant.';
                }
                if (duplicateName && trimmed.toLowerCase() === duplicateName.trim().toLowerCase()) {
                  return `A vendor named "${duplicateName}" already exists.`;
                }
                return true;
              },
            }}
          />
        </Stack>
      </FormProvider>
    </Dialog>
  );
};
